import moment from 'moment';
import {
  TIPO_PROGRAMACAO_DIARIA_DESLOCAMENTO,
  TIPO_PROGRAMACAO_DIARIA_INSPECAO,
  TIPO_PROGRAMACAO_DIARIA_MANUTENCAO_VEICULOS,
} from '../../../constants/ids';

export const MAX_GRUPOS_CELULA = 3;
export const MAX_NSIRS_PREVIEW = 2;

export const getResumoPorTipo = row =>
  (Array.isArray(row?.resumoPorTipo) ? row.resumoPorTipo : []);

export const labelNsirs = nsir => {
  if (!nsir) return '-';
  return nsir.codigo || nsir.titulo || nsir.equipamento || '-';
};

const amostraLength = grupo => {
  if (Array.isArray(grupo?.nsirs) && grupo.nsirs.length) return grupo.nsirs.length;
  if (Array.isArray(grupo?.itens) && grupo.itens.length) return grupo.itens.length;
  return 0;
};

export const amostraRestante = grupo => {
  const qtd = Number(grupo?.quantidade) || 0;
  return Math.max(0, qtd - amostraLength(grupo));
};

/**
 * Resumo tipado do item (lista no formulário / modal).
 * Deslocamento → De/Para (+ malha); Inspeção → NSIRs; Manutenção → placa;
 * demais → malha/cidade quando houver.
 */
export const formatItemDescricao = item => {
  if (!item) return '-';

  const inspecao = item.tipoId === TIPO_PROGRAMACAO_DIARIA_INSPECAO;
  const deslocamento = item.tipoId === TIPO_PROGRAMACAO_DIARIA_DESLOCAMENTO
    || !!(item.cidadeDe || item.cidadePara);
  const parts = [];

  if (deslocamento) {
    parts.push(`De: ${item.cidadeDe || '-'} → Para: ${item.cidadePara || '-'}`);
  } else if (item.nsirCodes?.length) {
    parts.push(`NSIRs: ${item.nsirCodes.join(', ')}`);
  } else if (item.nsirLabels?.length) {
    parts.push(`NSIRs: ${item.nsirLabels.join(', ')}`);
  } else if (item.veiculo || item.veiculoPlaca) {
    parts.push(`Veículo: ${item.veiculo || item.veiculoPlaca}`);
  }

  if (!inspecao && (item.malha || item.malhaNome)) {
    parts.push(item.malha || item.malhaNome);
  }

  if (!inspecao && !deslocamento && item.cidade) {
    parts.push(item.cidade);
  }

  return parts.length ? parts.join(' · ') : '-';
};

/**
 * Preview tipado de um grupo de resumoPorTipo (célula da listagem).
 * Inspeção → NSIRs; Deslocamento → cidades; Manutenção → placa; senão só N× Tipo.
 */
export const formatTipoPreview = grupo => {
  if (!grupo) return '';

  const qtd = Number(grupo.quantidade) || 0;
  const tipo = grupo.tipo || 'Outros';
  const prefix = `${qtd}× ${tipo}`;
  const rest = amostraRestante(grupo);

  if (grupo.tipoId === TIPO_PROGRAMACAO_DIARIA_INSPECAO && Array.isArray(grupo.nsirs) && grupo.nsirs.length) {
    const shown = grupo.nsirs.slice(0, MAX_NSIRS_PREVIEW).map(labelNsirs);
    const suffix = rest > 0 ? ` +${rest}` : '';
    return `${prefix} · ${shown.join(', ')}${suffix}`;
  }

  if (
    grupo.tipoId === TIPO_PROGRAMACAO_DIARIA_DESLOCAMENTO
    && Array.isArray(grupo.itens)
    && grupo.itens.length
  ) {
    const item = grupo.itens[0];
    const route = `${item.cidadeDe || '-'} → ${item.cidadePara || '-'}`;
    const malha = item.malha || item.malhaNome;
    const detail = malha ? `${route} · ${malha}` : route;
    const suffix = rest > 0 ? ` +${rest}` : '';
    return `${prefix} · ${detail}${suffix}`;
  }

  if (
    grupo.tipoId === TIPO_PROGRAMACAO_DIARIA_MANUTENCAO_VEICULOS
    && Array.isArray(grupo.itens)
    && grupo.itens.length
  ) {
    const detail = formatItemDescricao(grupo.itens[0]);
    const suffix = rest > 0 ? ` +${rest}` : '';
    return detail && detail !== '-'
      ? `${prefix} · ${detail}${suffix}`
      : `${prefix}${suffix}`;
  }

  if (Array.isArray(grupo.itens) && grupo.itens.length) {
    const detail = formatItemDescricao(grupo.itens[0]);
    if (detail && detail !== '-') {
      const suffix = rest > 0 ? ` +${rest}` : '';
      return `${prefix} · ${detail}${suffix}`;
    }
  }

  return prefix;
};

export const STATUS_LABEL = {
  SINCRONIZADA: 'Sincronizada',
  PENDENTE: 'Pendente',
  NAO_SINCRONIZADA: 'Não sincronizada',
  ERROR_SINCRONIZAR: 'Erro ao sincronizar',
};

export const statusLabel = status => STATUS_LABEL[status] || status || '-';

/** Epoch (DatePicker) ou string YYYY-MM-DD → referência do dia para API. */
export const toReferenceDayFormat = value => {
  if (value == null || value === '') return null;
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    return value.slice(0, 10);
  }
  const m = moment(Number(value) || value);
  return m.isValid() ? m.format('YYYY-MM-DD') : null;
};

/** String YYYY-MM-DD ou epoch → valor para DatePickerV2 (epoch). */
export const toReferenceDayEpoch = value => {
  if (value == null || value === '') return null;
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    const m = moment(value, 'YYYY-MM-DD');
    return m.isValid() ? m.valueOf() : null;
  }
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/** Item do form → DTO de escrita (sem labels de exibição). */
export const buildItemWritePayload = item => {
  if (!item) return null;
  const payload = {
    id: item.id,
    tipoId: item.tipoId,
  };
  if (item.cidade) payload.cidade = item.cidade;
  if (item.cidadeDe) payload.cidadeDe = item.cidadeDe;
  if (item.cidadePara) payload.cidadePara = item.cidadePara;
  if (item.malhaId) payload.malhaId = item.malhaId;
  if (item.nsirIds?.length) payload.nsirIds = item.nsirIds;
  if (item.veiculoId) payload.veiculoId = item.veiculoId;
  if (item.descricao) payload.descricao = item.descricao;
  return payload;
};

/** Estado do form → body POST/PUT programacao-diaria-dia. */
export const buildDiaWritePayload = values => ({
  ...(values.id ? { id: values.id } : {}),
  referenceDayFormat: toReferenceDayFormat(values.referenceDay),
  inspetorId: values.inspetorId,
  ...(values.inspetorAuxiliarId
    ? { inspetorAuxiliarId: values.inspetorAuxiliarId }
    : {}),
  itens: (values.itens || []).map(buildItemWritePayload).filter(Boolean),
});
